import { AsyncPipe, CommonModule, NgClass, NgIf } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LimitedCurrencyAmount } from '@lx/currency/shared/model/currency.model';
import { Amount } from '@lx/shared/data-access/types';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';
import { CardButtonActionMode, CardContainerType } from './card-container.types';
import { GoogleAnalyticsClickEventDirective } from '@lx/shared/google-analytics';
import { WeeksCounterComponent } from '../../forms';
import { CardSkeletonComponent, LiteButtonComponent, PriceLabelComponent } from '../../foundation';

@Component({
  standalone: true,
  selector: 'lx-card-container',
  templateUrl: './card-container.component.html',
  styleUrls: ['./card-container.component.scss'],
  imports: [
    LiteButtonComponent,
    WeeksCounterComponent,
    CommonModule,
    InputNumberModule,
    FormsModule,
    NgIf,
    PriceLabelComponent,
    NgClass,
    AsyncPipe,
    GoogleAnalyticsClickEventDirective,
    CardSkeletonComponent,
  ],
})
export class CardContainerComponent implements OnChanges {
  @Input() type: CardContainerType | undefined;
  @Input() title = '';
  @Input() actionMode: 'add' | 'edit' | 'remove' | undefined;
  @Input() weeks: number | undefined;
  @Input() price: Amount[] | LimitedCurrencyAmount[] | undefined;
  @Input() endPrice: Amount[] | LimitedCurrencyAmount[] | undefined;
  @Input() currency: string | undefined;
  @Input() discount = '';
  @Input() disabled = false;
  @Input() duration: number | undefined;
  @Input() priceLabel = '';
  @Input() footerVisible = true;
  @Input() styleClass = '';
  @Input() loading = false;

  // Optional custom height for the inner card skeleton (pixels or CSS value string).
  @Input() skeletonHeight = '12rem';

  /**
   * When this is active, the card won't have any background or border
   */
  @Input() plain = false;

  /**
   * The GA event name to be used for the button click
   */
  @Input() gaEventName = '';

  @Output() buttonClick = new EventEmitter<void>();
  @Output() changedWeeks = new EventEmitter<number>();

  buttonIcon = '';
  buttonText = '';
  weeksSuffix = '';

  mobile$: Observable<boolean> | undefined;

  private readonly breakpointObserverService = inject(BreakpointObserverService);

  constructor() {
    this.mobile$ = this.breakpointObserverService.isMobile$;
  }

  ngOnChanges({ actionMode, weeks }: SimpleChanges): void {
    if (actionMode?.currentValue) {
      this.prepareButton(actionMode.currentValue);
    }

    if (weeks?.currentValue) {
      this.weeksSuffix = this.getWeekSuffix(weeks?.currentValue);
    }
  }

  clickButton() {
    this.buttonClick.emit();
  }

  onWeeksChange(weeks: number) {
    this.weeksSuffix = this.getWeekSuffix(weeks);
    this.changedWeeks.emit(weeks);
  }

  private getWeekSuffix(weeks: number): string {
    return weeks === 1 ? $localize` week` : $localize` weeks`;
  }

  private prepareButton(actionMode: CardButtonActionMode) {
    switch (actionMode) {
      case 'add':
        this.buttonIcon = 'plus';
        this.buttonText = $localize`Add`;
        break;
      case 'edit':
        this.buttonIcon = 'pencil';
        this.buttonText = $localize`Edit`;
        break;
      case 'remove':
        this.buttonIcon = 'times';
        this.buttonText = $localize`Remove`;
        break;
    }
  }
}
